import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Clock, KeyRound, Heart } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Watch for our next update.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative mt-20 bg-ink text-oat">
      <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-clay text-oat">
                <KeyRound className="h-5 w-5" strokeWidth={2.25} />
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-serif text-xl font-semibold tracking-tight">KEYZ</span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-clay-200">Foundation Inc.</span>
              </span>
            </div>
            <p className="mt-6 max-w-md font-serif text-2xl leading-snug text-oat">
              Restoring Lives. Building Leaders. Strengthening Communities.
            </p>
            <p className="mt-4 max-w-md text-sm text-oat/70">
              A faith-based nonprofit rooted in mentorship, restoration, and community. Every soul carries a key — we help unlock it.
            </p>
            <form onSubmit={subscribe} className="mt-8 flex max-w-sm gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="footer-newsletter-input"
                className="flex-1 rounded-full border border-oat/20 bg-ink px-4 py-2.5 text-sm text-oat placeholder:text-oat/40 focus:border-clay focus:outline-none focus:ring-2 focus:ring-clay/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="rounded-full bg-clay px-5 py-2.5 text-sm font-bold text-oat transition-colors hover:bg-clay-600 disabled:opacity-60"
              >
                {loading ? "…" : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-serif text-lg text-oat">Explore</h4>
            <ul className="mt-5 space-y-3 text-sm text-oat/80">
              <li><Link data-testid="footer-link-about" to="/about" className="hover:text-clay-200">About</Link></li>
              <li><Link data-testid="footer-link-programs" to="/programs" className="hover:text-clay-200">Programs</Link></li>
              <li><Link data-testid="footer-link-events" to="/events" className="hover:text-clay-200">Events</Link></li>
              <li><Link data-testid="footer-link-stories" to="/stories" className="hover:text-clay-200">Success Stories</Link></li>
              <li><Link data-testid="footer-link-resources" to="/resources" className="hover:text-clay-200">Resource Center</Link></li>
              <li><Link data-testid="footer-link-impact" to="/impact" className="hover:text-clay-200">Impact Dashboard</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-serif text-lg text-oat">Reach Us</h4>
            <ul className="mt-5 space-y-4 text-sm text-oat/80">
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 text-clay-200" />
                <a href="mailto:keyzfoundationinc@gmail.com" data-testid="footer-email" className="hover:text-clay-200">
                  keyzfoundationinc@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 text-clay-200" />
                <div>
                  <div>Mon – Thu, 8:30 – 4:30</div>
                  <div>Friday, 8:30 – 12:00</div>
                </div>
              </li>
              <li className="flex items-center gap-4 pt-2">
                <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="grid h-9 w-9 place-items-center rounded-full border border-oat/20 hover:border-clay hover:text-clay-200">
                  <Instagram className="h-4 w-4" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noreferrer" data-testid="footer-facebook" className="grid h-9 w-9 place-items-center rounded-full border border-oat/20 hover:border-clay hover:text-clay-200">
                  <Facebook className="h-4 w-4" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-oat/15 pt-6 text-xs text-oat/50 md:flex-row md:items-center">
          <div>© {new Date().getFullYear()} KEYZ Foundation Inc. All rights reserved. 501(c)(3) non-profit.</div>
          <div className="inline-flex items-center gap-1.5">
            Built with <Heart className="h-3.5 w-3.5 text-clay-200" /> for the community.
          </div>
        </div>
      </div>
    </footer>
  );
}
